import { convertPresentation2 } from '@hyperion-framework/presentation-2-parser';

const emptyAnnotation = {
    id: 'https://hyperion/annotation',
    type: 'Annotation',
    behavior: [],
    label: null,
    thumbnail: [],
    summary: null,
    requiredStatement: null,
    metadata: [],
    seeAlso: [],
    homepage: null,
    logo: [],
    rendering: [],
    service: [],
    accessibility: [],
    audience: [],
    body: [],
    bodyValue: null,
    canonical: null,
    created: null,
    creator: [],
    generated: null,
    generator: [],
    modified: null,
    motivation: [],
    rights: [],
    stylesheet: null,
    target: [],
    timeMode: undefined,
    via: [],
    partOf: [],
};
const emptyAnnotationPage = {
    id: 'https://hyperion/annotation-page',
    type: 'AnnotationPage',
    behavior: [],
    motivation: null,
    label: null,
    thumbnail: [],
    summary: null,
    requiredStatement: null,
    metadata: [],
    rights: null,
    provider: [],
    items: [],
    seeAlso: [],
    homepage: null,
    logo: [],
    rendering: [],
    service: [],
};
const emptyCanvas = {
    id: 'https://hyperion/empty-canvas',
    type: 'Canvas',
    label: null,
    behavior: [],
    motivation: null,
    thumbnail: [],
    posterCanvas: null,
    accompanyingCanvas: null,
    placeholderCanvas: null,
    summary: null,
    requiredStatement: null,
    metadata: [],
    rights: null,
    navDate: null,
    provider: [],
    items: [],
    annotations: [],
    seeAlso: [],
    homepage: null,
    logo: [],
    partOf: [],
    rendering: [],
    service: [],
    duration: 0,
    height: 0,
    width: 0,
};
const emptyCollection = {
    id: 'https://hyperion/empty-collection',
    type: 'Collection',
    label: null,
    viewingDirection: 'left-to-right',
    behavior: [],
    motivation: null,
    thumbnail: [],
    posterCanvas: null,
    accompanyingCanvas: null,
    placeholderCanvas: null,
    summary: null,
    requiredStatement: null,
    metadata: [],
    rights: null,
    navDate: null,
    provider: [],
    items: [],
    annotations: [],
    seeAlso: [],
    homepage: null,
    logo: [],
    partOf: [],
    rendering: [],
    service: [],
    services: [],
};
const emptyManifest = {
    id: 'https://hyperion/empty-manifest',
    type: 'Manifest',
    annotations: [],
    behavior: [],
    homepage: null,
    items: [],
    label: null,
    logo: [],
    metadata: [],
    motivation: null,
    navDate: null,
    provider: [],
    partOf: [],
    posterCanvas: null,
    accompanyingCanvas: null,
    placeholderCanvas: null,
    rendering: [],
    requiredStatement: null,
    rights: null,
    seeAlso: [],
    service: [],
    services: [],
    start: null,
    structures: [],
    summary: null,
    thumbnail: [],
    viewingDirection: 'left-to-right',
};
const emptyRange = {
    id: 'https://hyperion/empty-canvas',
    type: 'Range',
    label: null,
    behavior: [],
    motivation: null,
    thumbnail: [],
    posterCanvas: null,
    accompanyingCanvas: null,
    placeholderCanvas: null,
    summary: null,
    requiredStatement: null,
    metadata: [],
    rights: null,
    navDate: null,
    provider: [],
    items: [],
    annotations: [],
    seeAlso: [],
    homepage: null,
    logo: [],
    partOf: [],
    rendering: [],
    service: [],
    start: null,
    supplementary: null,
    viewingDirection: 'left-to-right',
};

const types = [
    'Collection',
    'Manifest',
    'Canvas',
    'AnnotationPage',
    'AnnotationCollection',
    'Annotation',
    'ContentResource',
    'Range',
    'Service',
    'Selector',
];
function identifyResource(resource) {
    if (typeof resource === 'undefined' || resource === null) {
        throw new Error('Null or undefined is not a valid entity.');
    }
    if (Array.isArray(resource)) {
        throw new Error('Array is not a valid entity');
    }
    if (typeof resource !== 'object') {
        throw new Error(`${typeof resource} is not a valid entity`);
    }
    if (typeof resource.type === 'string') {
        const hasType = types.indexOf(resource.type);
        if (hasType !== -1) {
            return types[hasType];
        }
    }
    if (resource.profile) {
        return 'Service';
    }
    throw new Error('Resource type is not known');
}
class Traverse {
    constructor(traversals, options = {}) {
        this.traversals = Object.assign({ collection: [], manifest: [], canvas: [], annotationCollection: [], annotationPage: [], annotation: [], contentResource: [], choice: [], range: [], service: [] }, traversals);
        this.options = Object.assign({ allowUndefinedReturn: false }, options);
    }
    static all(traversal) {
        return new Traverse({
            collection: [traversal],
            manifest: [traversal],
            canvas: [traversal],
            annotationCollection: [traversal],
            annotationPage: [traversal],
            annotation: [traversal],
            contentResource: [traversal],
            choice: [traversal],
            range: [traversal],
            service: [traversal],
        });
    }
    traverseDescriptive(resource) {
        if (resource.thumbnail) {
            resource.thumbnail = resource.thumbnail.map(thumbnail => this.traverseType(thumbnail, this.traversals.contentResource));
        }
        return resource;
    }
    traverseLinking(resource) {
        if (resource.seeAlso) {
            resource.seeAlso = resource.seeAlso.map(content => this.traverseType(content, this.traversals.contentResource));
        }
        if (resource.service) {
            resource.service = resource.service.map(service => this.traverseType(service, this.traversals.service));
        }
        if (resource.services) {
            resource.services = resource.services.map(service => this.traverseType(service, this.traversals.service));
        }
        if (resource.logo) {
            resource.logo = resource.logo.map(content => this.traverseType(content, this.traversals.contentResource));
        }
        if (resource.homepage) {
            resource.homepage = this.traverseType(resource.homepage, this.traversals.contentResource);
        }
        if (resource.partOf) {
            resource.partOf = resource.partOf.map(partOf => {
                if (typeof partOf === 'string' || !partOf.type) {
                    return this.traverseType(partOf, this.traversals.contentResource);
                }
                if (partOf.type === 'Canvas') {
                    return this.traverseType(partOf, this.traversals.canvas);
                }
                if (partOf.type === 'AnnotationCollection') {
                    return this.traverseType(partOf, this.traversals.annotationCollection);
                }
                return this.traverseType(partOf, this.traversals.contentResource);
            });
        }
        if (resource.start) {
            resource.start = resource.start.map(start => this.traverseType(start, this.traversals.canvas));
        }
        if (resource.rendering) {
            resource.rendering = resource.rendering.map(content => this.traverseType(content, this.traversals.contentResource));
        }
        if (resource.supplementary) {
            resource.supplementary = resource.supplementary.map(content => this.traverseType(content, this.traversals.contentResource));
        }
        return resource;
    }
    traverseCollectionItems(collection) {
        collection.items.map((collectionOrManifest) => {
            if (collectionOrManifest.type === 'Collection') {
                return this.traverseCollection(collectionOrManifest);
            }
            return this.traverseManifest(collectionOrManifest);
        });
        return collection;
    }
    traverseCollection(collection) {
        return this.traverseType(this.traverseDescriptive(this.traverseLinking(this.traversePosterCanvas(this.traverseCollectionItems(collection)))), this.traversals.collection);
    }
    traverseManifestItems(manifest) {
        if (manifest.items) {
            manifest.items = manifest.items.map(canvas => this.traverseCanvas(canvas));
        }
        return manifest;
    }
    traverseManifestStructures(manifest) {
        if (manifest.structures) {
            manifest.structures = manifest.structures.map(range => this.traverseRange(range));
        }
        return manifest;
    }
    traverseManifest(manifest) {
        return this.traverseType(this.traverseManifestStructures(this.traversePosterCanvas(this.traverseDescriptive(this.traverseLinking(this.traverseManifestItems(manifest))))), this.traversals.manifest);
    }
    traverseCanvasItems(canvas) {
        canvas.items = (canvas.items || []).map((annotationPage) => {
            return this.traverseAnnotationPage(annotationPage);
        });
        return canvas;
    }
    traverseInlineAnnotationPages(resource) {
        if (resource.annotations) {
            resource.annotations = resource.annotations.map((annotationPage) => {
                return this.traverseAnnotationPage(annotationPage);
            });
        }
        return resource;
    }
    traverseCanvas(canvas) {
        return this.traverseType(this.traverseInlineAnnotationPages(this.traversePosterCanvas(this.traverseDescriptive(this.traverseLinking(this.traverseCanvasItems(canvas))))), this.traversals.canvas);
    }
    traverseAnnotationPageItems(annotationPage) {
        if (annotationPage.items) {
            annotationPage.items = annotationPage.items.map((annotation) => {
                return this.traverseAnnotation(annotation);
            });
        }
        return annotationPage;
    }
    traverseAnnotationPage(annotationPageJson) {
        return this.traverseType(this.traverseDescriptive(this.traverseLinking(this.traverseAnnotationPageItems(annotationPageJson))), this.traversals.annotationPage);
    }
    traverseAnnotationBody(annotation) {
        if (Array.isArray(annotation.body)) {
            annotation.body = annotation.body.map((annotationBody) => {
                return this.traverseContentResource(annotationBody);
            });
        }
        else if (annotation.body) {
            annotation.body = this.traverseContentResource(annotation.body);
        }
        return annotation;
    }
    traversePosterCanvas(json) {
        if (json.posterCanvas) {
            json.posterCanvas = this.traverseType(json.posterCanvas, this.traversals.canvas);
        }
        if (json.placeholderCanvas) {
            json.placeholderCanvas = this.traverseType(json.placeholderCanvas, this.traversals.canvas);
        }
        if (json.accompanyingCanvas) {
            json.accompanyingCanvas = this.traverseType(json.accompanyingCanvas, this.traversals.canvas);
        }
        return json;
    }
    traverseAnnotation(annotationJson) {
        return this.traverseType(this.traverseLinking(this.traverseAnnotationBody(annotationJson)), this.traversals.annotation);
    }
    traverseContentResourceLinking(contentResourceJson) {
        if (typeof contentResourceJson === 'string' || !contentResourceJson) {
            return contentResourceJson;
        }
        if (contentResourceJson && contentResourceJson.service) {
            contentResourceJson.service = (contentResourceJson.service || []).map(service => this.traverseType(service, this.traversals.service));
        }
        return contentResourceJson;
    }
    traverseContentResource(contentResourceJson) {
        return this.traverseType(this.traverseContentResourceLinking(contentResourceJson), this.traversals.contentResource);
    }
    traverseRangeRanges(range) {
        if (range.items) {
            range.items = range.items.map((rangeOrManifest) => {
                if (typeof rangeOrManifest === 'string') {
                    return this.traverseCanvas({ id: rangeOrManifest, type: 'Canvas' });
                }
                if (rangeOrManifest.type === 'Manifest') {
                    return this.traverseManifest(rangeOrManifest);
                }
                return this.traverseRange(rangeOrManifest);
            });
        }
        return range;
    }
    traverseRange(range) {
        return this.traverseType(this.traversePosterCanvas(this.traverseDescriptive(this.traverseLinking(this.traverseRangeRanges(range)))), this.traversals.range);
    }
    traverseType(object, traversals) {
        return traversals.reduce((acc, traversal) => {
            const returnValue = traversal(acc);
            if (typeof returnValue === 'undefined' && !this.options.allowUndefinedReturn) {
                return acc;
            }
            return returnValue;
        }, object);
    }
    traverseService(service) {
        return this.traverseType(service, this.traversals.service);
    }
    traverseUnknown(resource) {
        const type = identifyResource(resource);
        switch (type) {
            case 'Collection':
                return this.traverseCollection(resource);
            case 'Manifest':
                return this.traverseManifest(resource);
            case 'Canvas':
                return this.traverseCanvas(resource);
            case 'AnnotationPage':
                return this.traverseAnnotationPage(resource);
            case 'Annotation':
                return this.traverseAnnotation(resource);
            case 'ContentResource':
                return this.traverseContentResource(resource);
            case 'Range':
                return this.traverseRange(resource);
            case 'Service':
                return this.traverseService(resource);
            default:
                throw new Error(`Unknown or unsupported resource type of ${type}`);
        }
    }
}

const defaultEntities = {
    Collection: {},
    Manifest: {},
    Canvas: {},
    AnnotationPage: {},
    AnnotationCollection: {},
    Annotation: {},
    ContentResource: {},
    Range: {},
    Service: {},
    Selector: {},
};
function getDefaultEntities() {
    return {
        Collection: {},
        Manifest: {},
        Canvas: {},
        AnnotationPage: {},
        AnnotationCollection: {},
        Annotation: {},
        ContentResource: {},
        Range: {},
        Service: {},
        Selector: {},
    };
}
function getResource(entityOrString, type) {
    if (typeof entityOrString === 'string') {
        return { id: entityOrString, type };
    }
    if (!entityOrString.id) {
        throw new Error(`Invalid resource does not have an ID (${type})`);
    }
    return entityOrString;
}
function mapToEntities(entities) {
    return (type, defaultStringType) => {
        const storeType = entities[type] ? entities[type] : {};
        return (r) => {
            const resource = getResource(r, defaultStringType || type);
            if (resource && resource.id && type) {
                storeType[resource.id] = storeType[resource.id]
                    ? Object.assign({}, storeType[resource.id], resource)
                    : Object.assign({}, resource);
                return { id: resource.id, type: type === 'ContentResource' ? type : resource.type };
            }
            return resource;
        };
    };
}
function recordTypeInMapping(mapping) {
    return (type, defaultStringType) => {
        return (r) => {
            const { id, type: foundType } = getResource(r, defaultStringType || type);
            if (typeof id === 'undefined') {
                throw new Error('Found invalid entity without an ID.');
            }
            if (type === 'ContentResource') {
                mapping[id] = type;
            }
            else {
                mapping[id] = foundType;
            }
            return r;
        };
    };
}
function hash(object) {
    const text = JSON.stringify(object);
    let numHash = 5381, index = text.length;
    while (index) {
        numHash = (numHash * 33) ^ text.charCodeAt(--index);
    }
    const num = numHash >>> 0;
    const hexString = num.toString(16);
    if (hexString.length % 2) {
        return '0' + hexString;
    }
    return hexString;
}
function addMissingIdToContentResource(type) {
    return (resource) => {
        if (typeof resource === 'string') {
            return { id: resource, type };
        }
        if (!resource.id) {
            return Object.assign({ id: hash(resource), type }, resource);
        }
        if (!resource.type) {
            return Object.assign({ type }, resource);
        }
        return resource;
    };
}
function ensureDefaultFields(defaultResource) {
    return (resource) => {
        return Object.assign(Object.assign({}, defaultResource), resource);
    };
}
function ensureArray(maybeArray) {
    if (Array.isArray(maybeArray)) {
        return maybeArray;
    }
    return [maybeArray];
}
function ensureArrayOnAnnotation(annotation) {
    if (annotation.body) {
        annotation.body = ensureArray(annotation.body);
    }
    if (annotation.seeAlso) {
        annotation.seeAlso = ensureArray(annotation.seeAlso);
    }
    if (annotation.body) {
        annotation.body = ensureArray(annotation.body);
    }
    if (annotation.audience) {
        annotation.audience = ensureArray(annotation.audience);
    }
    if (annotation.accessibility) {
        annotation.accessibility = ensureArray(annotation.accessibility);
    }
    if (annotation.motivation) {
        annotation.motivation = ensureArray(annotation.motivation);
    }
    return annotation;
}
function normalize(unknownEntity) {
    const entity = convertPresentation2(unknownEntity);
    const entities = getDefaultEntities();
    const mapping = {};
    const addToEntities = mapToEntities(entities);
    const addToMapping = recordTypeInMapping(mapping);
    const traversal = new Traverse({
        collection: [
            ensureDefaultFields(emptyCollection),
            addToMapping('Collection'),
            addToEntities('Collection'),
        ],
        manifest: [
            ensureDefaultFields(emptyManifest),
            addToMapping('Manifest'),
            addToEntities('Manifest'),
        ],
        canvas: [
            ensureDefaultFields(emptyCanvas),
            addToMapping('Canvas'),
            addToEntities('Canvas'),
        ],
        annotationPage: [
            addMissingIdToContentResource('AnnotationPage'),
            ensureDefaultFields(emptyAnnotationPage),
            addToMapping('AnnotationPage'),
            addToEntities('AnnotationPage'),
        ],
        annotation: [
            addMissingIdToContentResource('Annotation'),
            ensureArrayOnAnnotation,
            addToMapping('Annotation'),
            addToEntities('Annotation'),
        ],
        contentResource: [
            addMissingIdToContentResource('ContentResource'),
            addToMapping('ContentResource'),
            addToEntities('ContentResource'),
        ],
        range: [
            ensureDefaultFields(emptyRange),
            addToMapping('Range', 'Canvas'),
            addToEntities('Range', 'Canvas'),
        ],
    });
    const resource = traversal.traverseUnknown(entity);
    return { entities, resource, mapping };
}

const UNSET = '__$HF_UNSET$__';
const UNWRAP = '__$HF_UNWRAP$__';
function resolveIfExists(state, url) {
    const request = state.hyperion.requests[url];
    const resourceType = state.hyperion.mapping[url];
    if (!resourceType ||
        (request && request.resourceUri && !state.hyperion.entities[resourceType][request.resourceUri])) {
        return undefined;
    }
    return state.hyperion.entities[resourceType][request ? request.resourceUri : url];
}
function serialisedFieldsToObject(fields) {
    const object = {};
    for (const [key, value] of fields) {
        if (key === UNWRAP) {
            return value;
        }
        if (value !== UNSET && typeof value !== 'undefined' && value !== null) {
            object[key] = value;
        }
    }
    return object;
}
function serialise(state, subject, config) {
    if (!subject.type || !subject.id) {
        throw new Error('Unknown entity');
    }
    if (!config[subject.type]) {
        throw new Error(`Serialiser not found for ${subject.type}`);
    }
    function flatten(sub) {
        const generator = config[sub.type];
        if (!generator) {
            return UNSET;
        }
        const resource = resolveIfExists(state, sub.id);
        if (!resource) {
            return UNSET;
        }
        const iterator = generator(resource, state);
        let current = iterator.next();
        while (!current.done) {
            const requestToHydrate = current.value;
            let next = UNSET;
            if (requestToHydrate) {
                if (Array.isArray(requestToHydrate)) {
                    const nextList = [];
                    for (const req of requestToHydrate) {
                        nextList.push(flatten(req));
                    }
                    next = nextList;
                }
                else {
                    next = flatten(requestToHydrate);
                }
            }
            current = iterator.next(next);
        }
        if (current.value === UNSET) {
            return UNSET;
        }
        return serialisedFieldsToObject(current.value);
    }
    return flatten(subject);
}

function languageString2to3(value) {
    if (!value) {
        return undefined;
    }
    const languages = Object.keys(value);
    if (languages.length === 0) {
        return undefined;
    }
    if (languages.length === 1) {
        const language = languages[0];
        if (!language) {
            return '';
        }
        const singleValue = (value[language] || []).join('');
        if (language === '@none' || language === 'none' || language === 'en') {
            return singleValue;
        }
        return {
            '@language': language,
            '@value': singleValue,
        };
    }
    return languages.map(language => {
        return {
            '@language': language,
            '@value': (value[language] || []).join(''),
        };
    });
}
function parseCanvasTarget(target) {
    if (Array.isArray(target)) {
        return target.map(t => parseCanvasTarget(t));
    }
    if (typeof target === 'string') {
        return target;
    }
    if (target.type && target.type === 'Canvas') {
        return target.id;
    }
    return target;
}
function unNestArray(oneOrArray, onlyOne = false) {
    if (!oneOrArray) {
        return undefined;
    }
    if (oneOrArray.length > 1 && !onlyOne) {
        return oneOrArray;
    }
    return oneOrArray[0] || undefined;
}
function convertService(service) {
    if (!service) {
        return undefined;
    }
    if (typeof service === 'string') {
        return {
            '@id': service,
        };
    }
    if ('@id' in service) {
        const newService = Object.assign({}, service);
        delete newService['@type'];
        return newService;
    }
    return {
        '@context': 'http://iiif.io/api/image/2/context.json',
        '@id': service.id,
        profile: `http://iiif.io/api/image/2/profiles/${service.profile}.json`,
    };
}
function technicalProperties$1(props, type) {
    return [
        ['@id', props.id],
        ['@type', type],
        ['format', props.format],
        ['height', props.height],
        ['width', props.width],
        ['viewingDirection', props.viewingDirection !== 'left-to-right' ? props.viewingDirection : undefined],
    ];
}
function* descriptiveProperties$1(prop) {
    const provider = prop.provider ? prop.provider[0] : undefined;
    return [
        ['label', languageString2to3(prop.label)],
        [
            'metadata',
            prop.metadata && prop.metadata.length
                ? prop.metadata.map(item => ({
                    label: languageString2to3(item.label) || '',
                    value: languageString2to3(item.value) || '',
                }))
                : undefined,
        ],
        ['description', languageString2to3(prop.summary)],
        ['thumbnail', unNestArray(yield prop.thumbnail)],
        ['navDate', prop.navDate],
        ['logo', provider ? unNestArray(provider.logo) : undefined],
        ['homepage', provider ? provider.homepage : undefined],
        ['attribution', prop.requiredStatement ? languageString2to3(prop.requiredStatement.value) : undefined],
    ];
}
function* linkingProperties$1(prop) {
    return [
        ['seeAlso', unNestArray(yield prop.seeAlso)],
        ['service', unNestArray((prop.service || []).map(convertService))],
        ['rendering', unNestArray(yield prop.rendering)],
        ['startCanvas', prop.start ? prop.start.id : undefined],
    ];
}
const serialiseConfigPresentation2 = {
    Manifest: function* (entity) {
        return [
            ...technicalProperties$1(entity, 'sc:Manifest'),
            ...(yield* descriptiveProperties$1(entity)),
            ...(yield* linkingProperties$1(entity)),
            [
                'sequences',
                [
                    {
                        '@id': `${entity.id}/sequence0`,
                        '@type': 'sc:Sequence',
                        canvases: yield entity.items,
                    },
                ],
            ],
            ['structures', yield entity.structures],
        ];
    },
    Canvas: function* (entity) {
        const paintingPage = yield entity.items;
        const resources = paintingPage[0];
        return [
            ...technicalProperties$1(entity, 'sc:Canvas'),
            ...(yield* descriptiveProperties$1(entity)),
            ...(yield* linkingProperties$1(entity)),
            ['images', resources ? [resources.resources] : undefined],
            [
                'annotations',
                entity.annotations && entity.annotations.length ? unNestArray(yield entity.annotations) : undefined,
            ],
        ];
    },
    AnnotationPage: function* (entity) {
        return [
            ...technicalProperties$1(entity, 'sc:AnnotationList'),
            ...(yield* descriptiveProperties$1(entity)),
            ['resources', entity.items && entity.items.length ? unNestArray(yield entity.items) : undefined],
        ];
    },
    Annotation: function* (entity) {
        return [
            ['@id', entity.id],
            ['@type', 'oa:Annotation'],
            ['motivation', 'sc:painting'],
            ['on', parseCanvasTarget(entity.target)],
            ['resource', unNestArray(yield entity.body, true)],
        ];
    },
    ContentResource: function* (entity) {
        switch (entity.type) {
            case 'Image':
                return [
                    ...technicalProperties$1(entity, 'dctypes:Image'),
                    ...(yield* descriptiveProperties$1(entity)),
                    ...(yield* linkingProperties$1(entity)),
                ];
            case 'Text':
            case 'Dataset':
            default:
                return [...technicalProperties$1(entity, undefined), ...(yield* descriptiveProperties$1(entity))];
        }
    },
    AnnotationCollection: function* (entity) {
        return [
            ['@id', entity.id],
            ['@type', 'sc:Layer'],
            ['label', languageString2to3(entity.label)],
        ];
    },
    Collection: function* (entity) {
        return [
            ...technicalProperties$1(entity, 'sc:Collection'),
            ...(yield* descriptiveProperties$1(entity)),
            ...(yield* linkingProperties$1(entity)),
            ['members', yield* entity.items],
        ];
    },
    Range: function* (entity) {
        const members = [];
        const canvases = [];
        if (entity.items) {
            for (const item of entity.items) {
                const canvas = yield item;
                members.push({
                    '@id': item.id,
                    '@type': item.type,
                    label: canvas ? canvas.label : undefined,
                    within: entity.id,
                });
                if (item.type === 'Canvas') {
                    canvases.push(item.id);
                }
            }
        }
        return [
            ...technicalProperties$1(entity, 'sc:Range'),
            ...(yield* descriptiveProperties$1(entity)),
            ...(yield* linkingProperties$1(entity)),
            ['canvases', canvases.length === members.length ? canvases : undefined],
            ['members', canvases.length !== members.length ? members : undefined],
        ];
    },
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function technicalProperties(entity) {
    return [
        ['id', entity.id],
        ['type', entity.type],
        ['format', entity.format],
        ['profile', entity.profile],
        ['height', entity.height],
        ['width', entity.width],
        ['duration', entity.duration || undefined],
        ['viewingDirection', entity.viewingDirection !== 'left-to-right' ? entity.viewingDirection : undefined],
        ['behavior', entity.behavior && entity.behavior.length ? entity.behavior : undefined],
        ['timeMode', entity.timeMode],
        ['motivation', entity.motivation],
    ];
}
function filterEmpty(item) {
    if (!item || item.length === 0) {
        return undefined;
    }
    return item;
}
function service2compat(service) {
    if (service && service.type && service.type === 'ImageService2') {
        const { id, type, profile } = service, _service = __rest(service, ["id", "type", "profile"]);
        return Object.assign({ '@id': id, '@type': type, profile: profile.startsWith('http') ? profile : `http://iiif.io/api/image/2/${profile}.json` }, _service);
    }
    return service;
}
function filterService2Compat(services) {
    if (!services || services.length === 0) {
        return undefined;
    }
    return services.map(service2compat);
}
function* descriptiveProperties(entity) {
    return [
        ['label', entity.label],
        ['metadata', filterEmpty(entity.metadata)],
        ['summary', entity.summary],
        ['requiredStatement', entity.requiredStatement],
        ['rights', entity.rights],
        ['navDate', entity.navDate],
        ['language', entity.language],
        ['thumbnail', filterEmpty(yield entity.thumbnail)],
        ['placeholderCanvas', yield entity.placeholderCanvas],
        ['accompanyingCanvas', yield entity.accompanyingCanvas],
        ['provider', filterEmpty(entity.provider)],
    ];
}
function* linkingProperties(entity) {
    return [
        ['seeAlso', filterEmpty(yield entity.seeAlso)],
        ['service', filterService2Compat(entity.service)],
        ['services', filterService2Compat(entity.services)],
        ['rendering', filterEmpty(yield entity.rendering)],
        ['supplementary', filterEmpty(yield entity.supplementary)],
        ['partOf', filterEmpty(entity.partOf)],
        ['start', entity.start],
    ];
}
const serialiseConfigPresentation3 = {
    Manifest: function* (entity) {
        return [
            ...technicalProperties(entity),
            ...(yield* descriptiveProperties(entity)),
            ...(yield* linkingProperties(entity)),
            ['items', yield entity.items],
            ['structures', filterEmpty(yield entity.structures)],
        ];
    },
    Canvas: function* (entity) {
        return [
            ...technicalProperties(entity),
            ...(yield* descriptiveProperties(entity)),
            ...(yield* linkingProperties(entity)),
            ['items', yield entity.items],
            ['annotations', filterEmpty(yield entity.annotations)],
        ];
    },
    AnnotationPage: function* (entity) {
        const entries = Object.entries(entity)
            .map(([key, item]) => {
            return [key, Array.isArray(item) ? filterEmpty(item) : item];
        })
            .filter(([key, value]) => {
            return key !== 'items';
        });
        return [
            ...entries,
            ...(yield* linkingProperties(entity)),
            ['items', yield entity.items],
        ];
    },
    Service: function* (entity) {
        return [[UNWRAP, service2compat(entity)]];
    },
    Annotation: function* (entity) {
        const entries = Object.entries(entity)
            .map(([key, item]) => {
            return [key, Array.isArray(item) ? filterEmpty(item) : item];
        })
            .filter(([key]) => {
            return key !== 'body';
        });
        return [...entries, ['body', yield entity.body]];
    },
    ContentResource: function* (entity) {
        return [
            ...technicalProperties(entity),
            ...(yield* descriptiveProperties(entity)),
            ...(yield* linkingProperties(entity)),
        ];
    },
    AnnotationCollection: function* (entity) {
        return [
            ['id', entity.id],
            ['type', 'AnnotationCollection'],
            ['label', entity.label],
        ];
    },
    Collection: function* (entity) {
        return [
            ...technicalProperties(entity),
            ...(yield* descriptiveProperties(entity)),
            ...(yield* linkingProperties(entity)),
            ['items', yield* entity.items],
        ];
    },
    Range: function* (entity) {
        const rangeItems = [];
        for (const item of entity.items) {
            if (item.type === 'Range') {
                rangeItems.push(yield item);
            }
            else {
                rangeItems.push(item);
            }
        }
        return [
            ...technicalProperties(entity),
            ...(yield* descriptiveProperties(entity)),
            ...(yield* linkingProperties(entity)),
            ['items', rangeItems],
            ['annotations', filterEmpty(yield entity.annotations)],
        ];
    },
};

export { Traverse, UNSET, UNWRAP, defaultEntities, emptyAnnotation, emptyAnnotationPage, emptyCanvas, emptyCollection, emptyManifest, emptyRange, getDefaultEntities, identifyResource, languageString2to3, normalize, serialise, serialiseConfigPresentation2, serialiseConfigPresentation3, serialisedFieldsToObject, types };
//# sourceMappingURL=index.es.js.map
